/**
 * Project archive — a soft delete for a whole board.
 *
 * `projects.archived_at` holds the moment a project was put away; empty means
 * active, the same "empty string is a real state" convention `tasks.pinned_at`
 * uses. Nothing is ever deleted: an archived project and all its cards are
 * simply left out of every list, count and view, and unarchiving puts them all
 * back exactly as they were.
 *
 * Two ways the rest of the app honours it:
 *
 * - **Projects** are fetched whole (one query, one cache entry) and split here
 *   on the client, because the picker needs both halves — the active boards to
 *   switch between, and the archived ones to bring back.
 * - **Cards** are filtered on the server through the relation
 *   (`ACTIVE_PROJECT_CARDS`), so an archived board's cards never leave the
 *   database for the views that aggregate across projects.
 */

/** PocketBase filter for "cards whose project is not archived". */
export const ACTIVE_PROJECT_CARDS = 'project.archived_at = ""';

type Archivable = { archived_at?: string | null };

export function isArchived(project: Archivable): boolean {
  return typeof project.archived_at === "string" && project.archived_at.trim() !== "";
}

/** The boards that show up anywhere in the app, in their existing order. */
export function activeProjects<T extends Archivable>(projects: readonly T[]): T[] {
  return projects.filter((project) => !isArchived(project));
}

/** The boards put away, most recently archived first — the likeliest one to want back. */
export function archivedProjects<T extends Archivable>(projects: readonly T[]): T[] {
  return projects
    .filter(isArchived)
    .sort((a, b) => (b.archived_at ?? "").localeCompare(a.archived_at ?? ""));
}
